import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/* Reading-pace auto-scroll: after a few seconds idle, the page glides down
   slowly like a human reading. Any interaction pauses it; it resumes on idle. */
const EXCLUDED = /^\/(admin|login|hub|demo\/os|restaurant\/login)/;
const SPEED = 26; // px per second — calm reading pace (public site)
const START_DELAY = 6000;
const RESUME_DELAY = 8000;

export default function AutoScrollReader() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (EXCLUDED.test(pathname)) return undefined;
    // Inside areas (clients, affiliates, restaurant owners) read dense info — glide much slower.
    const isInnerArea = /^\/(account|affiliates\/dashboard|restaurant)/.test(pathname);
    const startDelay = isInnerArea ? 25000 : START_DELAY;
    const speed = isInnerArea ? 12 : SPEED;
    let raf = null;
    let idleTimer = null;
    let last = null;
    let acc = 0;

    const stop = () => { if (raf) cancelAnimationFrame(raf); raf = null; last = null; acc = 0; };

    const step = (ts) => {
      if (last != null) {
        acc += ((ts - last) / 1000) * speed;
        const px = Math.floor(acc);
        if (px >= 1) {
          const max = document.documentElement.scrollHeight - window.innerHeight;
          if (window.scrollY >= max - 2) { stop(); return; }
          window.scrollBy({ top: px, left: 0, behavior: 'instant' });
          acc -= px;
        }
      }
      last = ts;
      raf = requestAnimationFrame(step);
    };

    const start = () => { if (!raf && !document.hidden) raf = requestAnimationFrame(step); };
    const onActivity = () => { stop(); clearTimeout(idleTimer); idleTimer = setTimeout(start, isInnerArea ? 18000 : RESUME_DELAY); };

    const evs = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    evs.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onActivity);
    idleTimer = setTimeout(start, startDelay);

    return () => {
      stop();
      clearTimeout(idleTimer);
      evs.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onActivity);
    };
  }, [pathname]);

  return null;
}
